import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import Navbar from "../components/common/Navbar";
import ConfirmationHeader from "../components/confirmation/ConfirmationHeader";
import ConfirmationForm from "../components/confirmation/ConfirmationForm";
import ConfirmationModal from "../components/confirmation/ConfirmationModal";
import AlertModal from "../components/common/AlertModal";

export default function Confirmation() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    invitationSide: null, // 'bride', 'groom', 'both'
    attendance: null, // 'yes', 'no', 'not_sure'
    plusOne: null, // boolean
    plusOneCount: "",
    children: null, // boolean
    childrenCount: 1, // number (default 1 if selected)
    childrenAges: [""], // array of strings
    requirements: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelection = (field, value) => {
    setFormData((prev) => {
      const newState = {
        ...prev,
        [field]: prev[field] === value ? null : value,
      };

      // Reset children logic if deselected
      if (field === "children" && newState[field] === null) {
        newState.childrenCount = 1;
        newState.childrenAges = [""];
      } else if (field === "children" && newState[field] === true) {
        newState.childrenCount = 1;
        newState.childrenAges = [""];
      }

      return newState;
    });
    // Clear error for this field when user makes selection
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleChildrenCountChange = (increment) => {
    setFormData((prev) => {
      const newCount = prev.childrenCount + increment;
      if (newCount < 1 || newCount > 5) return prev;

      const newAges = [...prev.childrenAges];
      if (increment > 0) {
        newAges.push("");
      } else {
        newAges.pop();
      }

      return {
        ...prev,
        childrenCount: newCount,
        childrenAges: newAges,
      };
    });
  };

  const handleChildAgeChange = (index, value) => {
    setFormData((prev) => {
      const newAges = [...prev.childrenAges];
      newAges[index] = value;
      return { ...prev, childrenAges: newAges };
    });
    // Clear error for this child age field when user starts typing
    if (errors[`childAge${index}`]) {
      setErrors((prev) => ({ ...prev, [`childAge${index}`]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Attendance validation (ALWAYS REQUIRED)
    if (!formData.attendance) {
      newErrors.attendance = "validation.attendanceRequired";
    }

    // Name validation - letters, spaces, hyphens only (REQUIRED ALWAYS)
    if (!formData.name.trim()) {
      newErrors.name = "validation.nameRequired";
    } else if (!/^[a-zA-Zа-яА-ЯёЁ\s'-]+$/.test(formData.name)) {
      newErrors.name = "validation.nameInvalid";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "validation.nameTooShort";
    }

    // Invitation Side validation (REQUIRED ALWAYS)
    if (!formData.invitationSide) {
      newErrors.invitationSide = "validation.invitationSideRequired";
    }

    // If user selected "no" for attendance, return after basic validation (name + attendance)
    if (formData.attendance === "no") {
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    // Below validations only apply if attendance is "yes" or "not_sure"

    // Email validation (REQUIRED)
    if (!formData.email.trim()) {
      newErrors.email = "validation.emailRequired";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "validation.emailInvalid";
      }
    }

    // Phone validation (OPTIONAL) - numbers, spaces, +, -, () allowed
    if (formData.phone.trim()) {
      const phoneRegex = /^[\d\s+\-()]+$/;
      if (!phoneRegex.test(formData.phone)) {
        newErrors.phone = "validation.phoneInvalid";
      } else if (formData.phone.replace(/\D/g, "").length < 6) {
        newErrors.phone = "validation.phoneTooShort";
      }
    }


    // Plus One selection validation (OPTIONAL)
    // Plus one count validation (OPTIONAL - only validate format if provided)
    if (formData.plusOne === true && formData.plusOneCount.trim()) {
      if (!/^\d+$/.test(formData.plusOneCount)) {
        newErrors.plusOneCount = "validation.plusOneCountInvalid";
      } else if (
        parseInt(formData.plusOneCount) < 1 ||
        parseInt(formData.plusOneCount) > 10
      ) {
        newErrors.plusOneCount = "validation.plusOneCountRange";
      }
    }

    // Children selection validation (OPTIONAL)
    // Children ages validation (OPTIONAL - only validate format if provided)
    if (formData.children === true) {
      formData.childrenAges.forEach((age, index) => {
        if (age.trim()) {
          if (!/^\d+$/.test(age)) {
            newErrors[`childAge${index}`] = "validation.childAgeInvalid";
          } else if (parseInt(age) < 0 || parseInt(age) > 18) {
            newErrors[`childAge${index}`] = "validation.childAgeRange";
          }
        }
      });
    }

    // Special requirements validation (OPTIONAL) - only check if provided
    if (formData.requirements.trim()) {
      const dangerousPattern = /<script|javascript:|onerror=|onclick=/i;
      if (dangerousPattern.test(formData.requirements)) {
        newErrors.requirements = "validation.requirementsInvalid";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      // Scroll to first error
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setLoading(true);

    try {
      await addDoc(collection(db, "confirmations"), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        invitationSide: formData.invitationSide,
        attendance: formData.attendance,
        plusOne: formData.plusOne,
        plusOneCount: formData.plusOne ? formData.plusOneCount : null,
        children: formData.children,
        childrenCount: formData.children ? formData.childrenCount : null,
        childrenAges: formData.children ? formData.childrenAges : null,
        requirements: formData.requirements,
        createdAt: serverTimestamp(),
      });

      setShowModal(true);
    } catch (error) {
      console.error("Error adding document: ", error);
      setAlertMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({
      name: "",
      email: "",
      phone: "",
      invitationSide: null,
      attendance: null,
      plusOne: null,
      plusOneCount: "",
      children: null,
      childrenCount: 1,
      childrenAges: [""],
      requirements: "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-test relative overflow-x-hidden">
      <Navbar />
      <div className="pt-16 md:pt-20">
        <ConfirmationHeader />
      </div>

      <ConfirmationForm
        formData={formData}
        errors={errors}
        handleChange={handleChange}
        handleSelection={handleSelection}
        handleChildrenCountChange={handleChildrenCountChange}
        handleChildAgeChange={handleChildAgeChange}
        handleSubmit={handleSubmit}
        loading={loading}
      />

      {showModal && (
        <ConfirmationModal
          onClose={closeModal}
          guestName={formData.name}
          showCalendar={formData.attendance === "yes"}
        />
      )}

      {alertMessage && (
        <AlertModal
          message={alertMessage}
          type="error"
          onClose={() => setAlertMessage(null)}
        />
      )}
    </div>
  );
}
